import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getIdToken } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { 
  FileSpreadsheet, Loader2, AlertCircle, CheckCircle, 
  Upload, ChevronDown, ChevronUp, Settings,
  Clock, PlayCircle, XCircle, RotateCcw, Trash2, Download, Save
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Station } from "@shared/schema";

type ColumnType = 'category' | 'percent' | 'index' | 'count' | 'fact' | 'ignore';

interface ColumnMapping {
  columnIndex: number;
  header: string;
  type: ColumnType;
}

interface PreviewResult {
  sheets: { name: string; rowCount: number }[];
  selectedSheet: string;
  headers: string[];
  dataStartRow: number;
  sampleRows: { rowNumber: number; values: any[] }[];
  totalDataRows: number;
}

interface GenericQueueItem {
  id: string;
  fileName: string;
  stationId: string;
  stationDisplayName: string;
  status: string;
  progress: number;
  factsFound: number;
  batchStatus: string | null;
  message: string | null;
  currentStep: string | null;
  errorMessage: string | null;
  promoted: boolean;
  batchJobId: string | null;
  createdAt: string;
  completedAt: string | null;
}

const DEFAULT_FACT_PROMPT = `You are converting data into a natural language fact for a media company.

Station: {station_name}
Audience: {audience_type}
Data: {data}

OUTPUT RULES
• Each fact must be ONE sentence only - start with capital letter, end with ONE period.
• Keep sentences natural and sales-friendly for sponsorship pitches.
• Include actual numbers from the data.

PERCENTAGE FACTS
• Format: "<percentage>% of {station_name} {audience_type} <natural action/characteristic>."
• Example: "72% of WETA viewers have a bachelor's degree or higher."

INDEX FACTS (when a value represents an index where 100 = average)
• If index > 100: "{station_name} {audience_type} are <lift>% more likely to <action> than the average adult."
• If index < 100: "{station_name} {audience_type} are <lift>% less likely to <action> than the average adult."
• Calculate lift as |index - 100|.
• Example: "GPB viewers are 45% more likely to purchase luxury vehicles than the average adult."

STYLE
• Use present tense verbs (shop at, purchase, own, use, have).
• Keep brand names and specific terms exactly as provided.
• Focus on the single most compelling data point from each row.`;

const COLUMN_TYPE_OPTIONS: { value: ColumnType; label: string; description: string }[] = [
  { value: 'ignore', label: 'Ignore', description: 'Skip this column' },
  { value: 'category', label: 'Category / Label', description: 'Row label or category name' },
  { value: 'percent', label: 'Percent', description: 'Percentage value (0-100)' },
  { value: 'index', label: 'Index', description: 'Index value (100 = average)' },
  { value: 'count', label: 'Count / Number', description: 'Raw numeric count' },
  { value: 'fact', label: 'Fact / Text', description: 'Text to include as-is' },
];

const COLUMN_TYPE_COLORS: Record<ColumnType, string> = {
  ignore: 'bg-gray-100 text-gray-500',
  category: 'bg-blue-100 text-blue-700',
  percent: 'bg-green-100 text-green-700',
  index: 'bg-purple-100 text-purple-700',
  count: 'bg-orange-100 text-orange-700',
  fact: 'bg-yellow-100 text-yellow-700',
};

export default function GenericProcessor() {
  const { toast } = useToast();
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [base64FileData, setBase64FileData] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [dataStartRow, setDataStartRow] = useState<number>(1);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  
  const [selectedStationId, setSelectedStationId] = useState("");
  const [stationDisplayName, setStationDisplayName] = useState("");
  const [format, setFormat] = useState<'listener' | 'viewer'>('listener');
  const [citationText, setCitationText] = useState("");
  const [showPromptSettings, setShowPromptSettings] = useState(false);
  const [customPrompt, setCustomPrompt] = useState(DEFAULT_FACT_PROMPT);
  const [isSavingFactPrompt, setIsSavingFactPrompt] = useState(false);
  
  const [uploadStepExpanded, setUploadStepExpanded] = useState(true);
  const [mappingStepExpanded, setMappingStepExpanded] = useState(true);
  const [promotingItems, setPromotingItems] = useState<Set<string>>(new Set());
  const [activeActionItemId, setActiveActionItemId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  interface GenericSettingsResponse {
    id: string;
    structurePrompt: string;
    factPrompt: string | null;
    defaultPrompt: string;
    defaultFactPrompt: string;
    createdAt: string;
    updatedAt: string;
  }

  const { data: genericSettings } = useQuery<GenericSettingsResponse>({
    queryKey: ['/api/generic-settings'],
  });

  useEffect(() => {
    if (genericSettings?.factPrompt) {
      setCustomPrompt(genericSettings.factPrompt);
    }
  }, [genericSettings]);

  const { data: stations = [] } = useQuery<Station[]>({
    queryKey: ['/api/stations'],
  });

  const { data: queueItems = [], isLoading: isLoadingQueue, refetch } = useQuery<GenericQueueItem[]>({
    queryKey: ['/api/generic-queue'],
    refetchInterval: 3000,
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx')) {
      toast({
        title: "Invalid File",
        description: "Please upload an Excel file (.xlsx format only)",
        variant: "destructive",
      });
      e.target.value = '';
      return;
    }
    
    setSelectedFile(file);
    setPreview(null);
    setColumnMappings([]);
    setBase64FileData(null);
    const year = new Date().getFullYear();
    const month = new Date().toLocaleString('default', { month: 'long' });
    setCitationText(`${file.name.replace(/\.(xlsx|xls)$/i, '')}, ${month} ${year}`);
    
    setIsLoadingPreview(true);
    try {
      const base64 = await fileToBase64(file);
      setBase64FileData(base64);
      
      const token = await getIdToken();
      const response = await fetch('/api/generic-processor/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ fileData: base64 }),
      });
      
      if (!response.ok) throw new Error('Failed to preview file');
      
      const previewData: PreviewResult = await response.json();
      setPreview(previewData);
      setDataStartRow(previewData.dataStartRow);
      setSelectedSheet(previewData.selectedSheet);
      
      const initialMappings: ColumnMapping[] = previewData.headers.map((header, idx) => ({
        columnIndex: idx,
        header,
        type: guessColumnType(header, previewData.sampleRows, idx) as ColumnType,
      }));
      setColumnMappings(initialMappings);
      
      setUploadStepExpanded(false);
      setMappingStepExpanded(true);
      
      toast({
        title: "File Loaded",
        description: `Found ${previewData.totalDataRows.toLocaleString()} data rows across ${previewData.sheets.length} sheet(s)`,
      });
    } catch (error) {
      toast({
        title: "Preview Failed",
        description: error instanceof Error ? error.message : "Failed to read the file",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSheetChange = async (newSheet: string) => {
    if (!base64FileData || newSheet === selectedSheet) return;
    
    setSelectedSheet(newSheet);
    setIsLoadingPreview(true);
    
    try {
      const token = await getIdToken();
      const response = await fetch('/api/generic-processor/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ fileData: base64FileData, sheetName: newSheet }),
      });
      
      if (!response.ok) throw new Error('Failed to load sheet');
      
      const previewData: PreviewResult = await response.json();
      setPreview(previewData);
      setDataStartRow(previewData.dataStartRow);
      
      const initialMappings: ColumnMapping[] = previewData.headers.map((header, idx) => ({
        columnIndex: idx,
        header,
        type: guessColumnType(header, previewData.sampleRows, idx) as ColumnType,
      }));
      setColumnMappings(initialMappings);
    } catch (error) {
      toast({
        title: "Sheet Load Failed",
        description: error instanceof Error ? error.message : "Failed to load sheet",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const updateColumnType = (columnIndex: number, type: ColumnType) => {
    setColumnMappings(prev => 
      prev.map(m => m.columnIndex === columnIndex ? { ...m, type } : m)
    );
  };

  const addToQueue = useMutation({
    mutationFn: async () => {
      if (!selectedFile || !selectedStationId || !base64FileData || !preview) {
        throw new Error("Missing required data for queue submission");
      }

      const selectedStation = stations.find(s => s.id === selectedStationId);
      const activeMappings = columnMappings.filter(m => m.type !== 'ignore');
      
      if (activeMappings.length === 0) {
        throw new Error("Please map at least one column before submitting");
      }

      const queueItem = await apiRequest('/api/generic-queue', 'POST', {
        fileName: selectedFile.name,
        fileData: base64FileData,
        stationId: selectedStationId,
        stationDisplayName: stationDisplayName.trim() || selectedStation?.name || '',
        format: format,
        citationText: citationText,
        customPrompt: customPrompt,
        columnMappings: JSON.stringify(activeMappings),
        dataStartRow: dataStartRow,
        sheetName: selectedSheet,
      });

      await apiRequest(`/api/generic-queue/${queueItem.id}/approve`, 'POST', {});
      return queueItem;
    },
    onSuccess: () => {
      toast({
        title: "Added to Queue",
        description: "Your file has been added to the processing queue.",
      });
      resetForm();
      refetch();
    },
    onError: (error: Error) => {
      toast({
        title: "Queue Submission Failed",
        description: error.message || "Failed to submit to processing queue.",
        variant: "destructive",
      });
    },
  });

  const retryQueueItem = useMutation({
    mutationFn: async (id: string) => {
      setActiveActionItemId(id);
      return apiRequest(`/api/generic-queue/${id}/retry`, 'POST', {});
    },
    onSuccess: () => {
      setActiveActionItemId(null);
      queryClient.invalidateQueries({ queryKey: ['/api/generic-queue'] });
      toast({ title: "Retry Started", description: "Processing has been restarted." });
    },
    onError: (error: Error) => {
      setActiveActionItemId(null);
      toast({ title: "Retry Failed", description: error.message, variant: "destructive" });
    },
  });

  const reprocessQueueItem = useMutation({
    mutationFn: async (id: string) => {
      setActiveActionItemId(id);
      return apiRequest(`/api/generic-queue/${id}/reprocess`, 'POST', {});
    },
    onSuccess: () => {
      setActiveActionItemId(null);
      queryClient.invalidateQueries({ queryKey: ['/api/generic-queue'] });
      toast({ title: "Reprocess Complete", description: "Batch results have been re-downloaded and reprocessed." });
    },
    onError: (error: Error) => {
      setActiveActionItemId(null);
      toast({ title: "Reprocess Failed", description: error.message, variant: "destructive" });
    },
  });

  const deleteQueueItem = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest(`/api/generic-queue/${id}`, 'DELETE');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/generic-queue'] });
      toast({ title: "Deleted", description: "Queue item has been removed." });
    },
  });

  const cancelQueueItem = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest(`/api/generic-queue/${id}/cancel`, 'POST', {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/generic-queue'] });
      toast({ title: "Processing Cancelled", description: "Queue item has been cancelled." });
    },
  });

  const downloadFile = async (item: GenericQueueItem) => {
    try {
      const token = await getIdToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`/api/generic-queue/${item.id}/download`, { headers });
      if (!response.ok) throw new Error('Failed to download file');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.stationDisplayName}_facts.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({ 
        title: "Download Failed", 
        description: error instanceof Error ? error.message : "Failed to download file.", 
        variant: "destructive" 
      });
    }
  };

  const handlePromote = async (item: GenericQueueItem) => {
    setPromotingItems(prev => new Set(prev).add(item.id));
    
    try {
      await apiRequest(`/api/generic-queue/${item.id}/promote`, 'POST', { stationId: item.stationId });
      toast({ title: "Facts Promoted", description: "Facts have been successfully promoted to the knowledge base." });
      queryClient.invalidateQueries({ queryKey: ['/api/generic-queue'] });
    } catch (error) {
      toast({ 
        title: "Promotion Failed", 
        description: error instanceof Error ? error.message : "Failed to promote facts.", 
        variant: "destructive" 
      });
    } finally {
      setPromotingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  const handleSaveFactPrompt = async () => {
    setIsSavingFactPrompt(true);
    try {
      const token = await getIdToken();
      const response = await fetch('/api/generic-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ factPrompt: customPrompt }),
      });
      
      if (!response.ok) throw new Error('Failed to save prompt');
      
      queryClient.invalidateQueries({ queryKey: ['/api/generic-settings'] });
      toast({ title: "Prompt Saved", description: "Your custom fact prompt has been saved as the default." });
    } catch (error) {
      toast({ title: "Save Failed", description: "Failed to save the prompt. Please try again.", variant: "destructive" });
    } finally {
      setIsSavingFactPrompt(false);
    }
  };

  const handleResetFactPrompt = () => {
    setCustomPrompt(DEFAULT_FACT_PROMPT);
    toast({ title: "Prompt Reset", description: "The fact prompt has been reset to the built-in default." });
  };

  const resetForm = () => {
    setPreview(null);
    setColumnMappings([]);
    setSelectedFile(null);
    setBase64FileData(null);
    setSelectedStationId("");
    setStationDisplayName("");
    setCitationText("");
    setUploadStepExpanded(true);
    setMappingStepExpanded(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const activeMappingCount = columnMappings.filter(m => m.type !== 'ignore').length;

  const getStatusBadge = (status: string, batchStatus?: string | null) => {
    const statusConfig: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: any }> = {
      pending: { variant: "secondary", icon: Clock },
      approved: { variant: "default", icon: PlayCircle },
      processing: { variant: "default", icon: Loader2 },
      completed: { variant: "outline", icon: CheckCircle },
      error: { variant: "destructive", icon: AlertCircle },
      cancelled: { variant: "secondary", icon: XCircle },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className={`h-3 w-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {status}
        {batchStatus && status === 'processing' && (
          <span className="ml-1 text-xs opacity-70">({batchStatus})</span>
        )}
      </Badge>
    );
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const getElapsedTime = (item: GenericQueueItem) => {
    if (!item.createdAt) return '-';
    const startTime = new Date(item.createdAt).getTime();
    const endTime = (item.status === 'completed' || item.status === 'error' || item.status === 'cancelled')
      ? (item.completedAt ? new Date(item.completedAt).getTime() : Date.now())
      : Date.now();
    return formatTime(Math.floor((endTime - startTime) / 1000));
  };

  return (
    <div className="space-y-4">
      {/* Step 1: Upload File */}
      <Card>
        <Collapsible open={uploadStepExpanded} onOpenChange={setUploadStepExpanded}>
          <CardHeader className="py-3">
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${preview ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {preview ? <CheckCircle className="h-3.5 w-3.5" /> : '1'}
                  </div>
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Upload className="h-4 w-4" />
                      Upload File
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {preview 
                        ? `${selectedFile?.name || 'File'} loaded - ${preview.totalDataRows.toLocaleString()} data rows`
                        : 'Upload an Excel file (.xlsx) to get started'
                      }
                    </CardDescription>
                  </div>
                </div>
                {uploadStepExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <div className="space-y-2">
                <Label className="text-sm">Excel File (.xlsx only)</Label>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  className="cursor-pointer h-9"
                  disabled={isLoadingPreview}
                />
                {isLoadingPreview && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Reading file and detecting structure...
                  </div>
                )}
                {selectedFile && !isLoadingPreview && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <FileSpreadsheet className="h-3.5 w-3.5" />
                    {selectedFile.name}
                  </div>
                )}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Step 2: Map Columns & Configure — always visible */}
      <Card className={preview ? "border-blue-200" : ""}>
        <Collapsible open={mappingStepExpanded} onOpenChange={setMappingStepExpanded}>
          <CardHeader className="py-3">
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${preview ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                    2
                  </div>
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Settings className="h-4 w-4" />
                      Map Columns & Configure
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {preview
                        ? `${activeMappingCount} of ${columnMappings.length} columns mapped - ${preview.totalDataRows.toLocaleString()} rows to process`
                        : 'Upload a file in Step 1 to map columns. AI prompt can be edited anytime.'
                      }
                    </CardDescription>
                  </div>
                </div>
                {mappingStepExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              {/* Sheet selector and data start row — disabled without file */}
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${!preview ? 'opacity-40 pointer-events-none' : ''}`}>
                {(preview?.sheets?.length ?? 0) > 1 && (
                  <div className="space-y-2">
                    <Label className="text-sm">Sheet</Label>
                    <Select value={selectedSheet} onValueChange={handleSheetChange}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(preview?.sheets || []).map(s => (
                          <SelectItem key={s.name} value={s.name}>
                            {s.name} ({s.rowCount} rows)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-sm">Data starts at row</Label>
                  <Input
                    type="number"
                    min={1}
                    value={dataStartRow}
                    onChange={e => setDataStartRow(parseInt(e.target.value) || 1)}
                    className="h-9"
                    disabled={!preview}
                  />
                  <p className="text-xs text-gray-400">Row {dataStartRow - 1} will be used as headers</p>
                </div>
              </div>

              {/* Column mapping — disabled without file */}
              <div className={!preview ? 'opacity-40 pointer-events-none' : ''}>
                <Label className="text-sm mb-2 block">Column Mappings</Label>
                {!preview ? (
                  <div className="p-4 bg-gray-50 rounded border text-center text-xs text-gray-400">
                    <FileSpreadsheet className="h-8 w-8 mx-auto mb-1.5 opacity-40" />
                    Upload a file in Step 1 to see columns
                  </div>
                ) : (
                  <>
                    <div className="p-2 bg-gray-50 rounded border text-xs mb-2">
                      <span className="text-gray-600">Assign a type to each column. Ignored columns won't be sent to the AI. Tip: at minimum, map one <strong>Category</strong> column and one data column (Percent, Index, or Count).</span>
                    </div>
                    <ScrollArea className="max-h-[200px]">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {columnMappings.map((mapping) => (
                          <div key={mapping.columnIndex} className="flex items-center gap-2 p-1.5 rounded border bg-white">
                            <span className={`text-xs font-medium truncate flex-1 px-1.5 py-0.5 rounded ${COLUMN_TYPE_COLORS[mapping.type]}`} title={mapping.header}>
                              {mapping.header}
                            </span>
                            <Select 
                              value={mapping.type} 
                              onValueChange={(v: ColumnType) => updateColumnType(mapping.columnIndex, v)}
                            >
                              <SelectTrigger className="h-7 w-[130px] text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {COLUMN_TYPE_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    <div>
                                      <span className="font-medium">{opt.label}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </>
                )}
              </div>

              {/* Data preview table — disabled without file */}
              {preview && (
                <div>
                  <Label className="text-sm mb-2 block">Data Preview (first {preview.sampleRows.length} rows)</Label>
                  <ScrollArea className="h-[250px] border rounded">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs w-[50px] sticky left-0 bg-white z-10">Row</TableHead>
                            {columnMappings.filter(m => m.type !== 'ignore').map(m => (
                              <TableHead key={m.columnIndex} className="text-xs whitespace-nowrap">
                                <div className="flex flex-col gap-0.5">
                                  <span>{m.header}</span>
                                  <Badge variant="outline" className={`text-[10px] w-fit ${COLUMN_TYPE_COLORS[m.type]}`}>
                                    {m.type}
                                  </Badge>
                                </div>
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {preview.sampleRows.slice(0, 20).map((row) => (
                            <TableRow key={row.rowNumber}>
                              <TableCell className="text-xs text-gray-400 sticky left-0 bg-white">{row.rowNumber}</TableCell>
                              {columnMappings.filter(m => m.type !== 'ignore').map(m => (
                                <TableCell key={m.columnIndex} className="text-xs whitespace-nowrap">
                                  {row.values[m.columnIndex] || ''}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Station and processing config — disabled without file */}
              <div className={`pt-4 border-t space-y-4 ${!preview ? 'opacity-40 pointer-events-none' : ''}`}>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-sm">Station</Label>
                    <Select value={selectedStationId} onValueChange={setSelectedStationId} disabled={!preview}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select a station" />
                      </SelectTrigger>
                      <SelectContent>
                        {stations.map((station) => (
                          <SelectItem key={station.id} value={station.id}>
                            {station.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">Station Display Name (Optional)</Label>
                    <Input
                      placeholder="Override station name in facts"
                      value={stationDisplayName}
                      onChange={(e) => setStationDisplayName(e.target.value)}
                      className="h-9"
                      disabled={!preview}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">Format</Label>
                    <Select value={format} onValueChange={(v: 'listener' | 'viewer') => setFormat(v)} disabled={!preview}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="listener">Radio (Listener)</SelectItem>
                        <SelectItem value="viewer">TV (Viewer)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">Citation Text</Label>
                    <Input
                      placeholder="Source citation for facts"
                      value={citationText}
                      onChange={(e) => setCitationText(e.target.value)}
                      className="h-9"
                      disabled={!preview}
                    />
                  </div>
                </div>
              </div>

              {/* Prompt Settings — always editable */}
              <div className="pt-4 border-t">
                <Collapsible open={showPromptSettings} onOpenChange={setShowPromptSettings}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-between px-2 h-8">
                      <span className="flex items-center gap-2 text-xs">
                        <Settings className="h-3.5 w-3.5" />
                        AI Prompt Settings
                      </span>
                      {showPromptSettings ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-2 space-y-2">
                    <Textarea
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      rows={6}
                      className="text-xs font-mono"
                    />
                    <div className="p-2 bg-gray-50 rounded border text-xs">
                      <p className="font-medium text-gray-700 mb-1">Available Template Variables:</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-gray-600">
                        <div><code className="bg-blue-100 text-blue-700 px-1 rounded">{'{station_name}'}</code> The station name (e.g. "GPB")</div>
                        <div><code className="bg-blue-100 text-blue-700 px-1 rounded">{'{audience_type}'}</code> "listeners" or "viewers" based on format</div>
                        <div><code className="bg-green-100 text-green-700 px-1 rounded">{'{category}'}</code> Value from columns mapped as Category</div>
                        <div><code className="bg-green-100 text-green-700 px-1 rounded">{'{percent}'}</code> Values from columns mapped as Percent (e.g. "Adults 18+: 72.5%")</div>
                        <div><code className="bg-purple-100 text-purple-700 px-1 rounded">{'{index}'}</code> Values from columns mapped as Index (e.g. "Adults 18+: 145.0")</div>
                        <div><code className="bg-orange-100 text-orange-700 px-1 rounded">{'{count}'}</code> Values from columns mapped as Count</div>
                        <div><code className="bg-red-100 text-red-700 px-1 rounded">{'{fact_type}'}</code> Type of fact: "percentage", "index", or "count"</div>
                        <div><code className="bg-yellow-100 text-yellow-700 px-1 rounded">{'{fact}'}</code> Text from columns mapped as Fact/Text</div>
                        <div><code className="bg-gray-200 text-gray-700 px-1 rounded">{'{data}'}</code> All mapped column data combined (category + percent + index + count + fact)</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={handleSaveFactPrompt}
                        disabled={isSavingFactPrompt}
                      >
                        {isSavingFactPrompt ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                        Save as Default
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={handleResetFactPrompt}>
                        <RotateCcw className="h-3 w-3" />
                        Reset to Built-in
                      </Button>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
              
              {/* Submit buttons — disabled without file */}
              <div className={`flex justify-between items-center pt-4 border-t ${!preview ? 'opacity-40 pointer-events-none' : ''}`}>
                <div className="text-xs text-gray-500">
                  {preview
                    ? `${activeMappingCount} columns mapped, ${preview.totalDataRows.toLocaleString()} rows will be processed`
                    : 'Upload a file to enable submission'
                  }
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={resetForm} disabled={!preview}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="gap-2"
                    onClick={() => addToQueue.mutate()}
                    disabled={addToQueue.isPending || !selectedStationId || activeMappingCount === 0 || !preview}
                  >
                    {addToQueue.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Adding to Queue...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-3.5 w-3.5" />
                        {!preview 
                          ? 'Upload a file first'
                          : selectedStationId 
                            ? `Send to Batch API (${preview.totalDataRows.toLocaleString()} rows)`
                            : 'Select a station first'
                        }
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Step 3: Processing Queue */}
      <Card>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-gray-100 text-gray-700">
                3
              </div>
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="h-4 w-4" />
                  Processing Queue
                </CardTitle>
                <CardDescription className="text-xs">
                  {queueItems.length} total items
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {queueItems.some(item => item.status === 'processing' && item.progress > 0 && item.progress < 100) && (
            <div className="w-full mb-4">
              {queueItems
                .filter(item => item.status === 'processing' && item.progress > 0 && item.progress < 100)
                .map((item) => (
                  <div key={item.id} className="space-y-1.5 mb-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Processing: {item.fileName}</span>
                      <span className="text-gray-500">{Math.round(item.progress || 0)}%</span>
                    </div>
                    <Progress value={item.progress || 0} className="w-full h-1.5" />
                    {item.currentStep && (
                      <p className="text-xs text-gray-500">{item.currentStep}</p>
                    )}
                  </div>
                ))}
            </div>
          )}

          {isLoadingQueue ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : queueItems.length === 0 ? (
            <div className="border rounded-lg p-6 text-center text-gray-500">
              <FileSpreadsheet className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No files in the queue</p>
              <p className="text-xs mt-1">Upload a file and map columns to get started</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">File Name</TableHead>
                    <TableHead className="text-xs">Station</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Facts</TableHead>
                    <TableHead className="text-xs">Time</TableHead>
                    <TableHead className="text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queueItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium max-w-[150px] truncate text-sm">
                        {item.fileName}
                      </TableCell>
                      <TableCell className="text-sm">{item.stationDisplayName}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(item.status, item.batchStatus)}
                          {item.status === 'error' && item.errorMessage && (
                            <span className="text-xs text-red-500 max-w-[150px] truncate" title={item.errorMessage}>
                              {item.errorMessage}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.factsFound > 0 ? item.factsFound.toLocaleString() : '-'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {getElapsedTime(item)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1.5 flex-wrap">
                          {item.status === 'processing' && (
                            <Button size="sm" variant="outline" onClick={() => cancelQueueItem.mutate(item.id)} className="h-7 text-xs px-2">
                              <XCircle className="h-3 w-3" />
                            </Button>
                          )}
                          {(item.status === 'completed' || (item.status === 'error' && item.factsFound > 0)) && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => downloadFile(item)} className="h-7 text-xs px-2">
                                <Download className="h-3 w-3" />
                              </Button>
                              {item.status === 'completed' && !item.promoted && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handlePromote(item)}
                                  className="text-green-600 hover:text-green-700 h-7 text-xs px-2"
                                  disabled={promotingItems.has(item.id)}
                                >
                                  {promotingItems.has(item.id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                                </Button>
                              )}
                              {item.promoted && (
                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-800 h-7 px-2">
                                  Promoted
                                </Badge>
                              )}
                            </>
                          )}
                          {(item.status === 'error' || item.status === 'cancelled' || (item.status === 'completed' && !item.promoted)) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => retryQueueItem.mutate(item.id)}
                              className="text-orange-600 hover:text-orange-700 h-7 text-xs px-2"
                              disabled={activeActionItemId === item.id && retryQueueItem.isPending}
                              title={item.status === 'completed' ? "Resubmit to OpenAI with current model settings (uses API credits)" : "Retry processing"}
                            >
                              <RotateCcw className={`h-3 w-3 ${activeActionItemId === item.id && retryQueueItem.isPending ? 'animate-spin' : ''}`} />
                            </Button>
                          )}
                          {(item.status === 'completed' || item.status === 'error' || item.status === 'cancelled') && (
                            <Button size="sm" variant="outline" onClick={() => deleteQueueItem.mutate(item.id)} className="h-7 text-xs px-2">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result?.toString().split(',')[1];
      if (base64) resolve(base64);
      else reject(new Error('Failed to read file'));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function guessColumnType(header: string, sampleRows: { values: any[] }[], colIdx: number): ColumnType {
  const headerLower = header.toLowerCase();
  
  if (headerLower.includes('index') || headerLower.includes('idx')) return 'index';
  if (headerLower.includes('%') || headerLower.includes('pct') || headerLower.includes('percent') || headerLower.includes('share')) return 'percent';
  if (headerLower.includes('count') || headerLower.includes('total') || headerLower.includes('population') || headerLower.includes('sample')) return 'count';
  
  const sampleValues = sampleRows.slice(0, 10).map(r => r.values[colIdx]).filter(v => v !== '' && v !== undefined && v !== null);
  
  if (sampleValues.length === 0) return 'ignore';
  
  const numericCount = sampleValues.filter(v => {
    const str = String(v).replace(/[,%$]/g, '');
    return !isNaN(parseFloat(str)) && isFinite(parseFloat(str));
  }).length;
  
  const isFirstColumn = colIdx === 0;
  const stringCount = sampleValues.filter(v => typeof v === 'string' && v.length > 2 && isNaN(parseFloat(String(v).replace(/[,%$]/g, '')))).length;
  
  if (isFirstColumn && stringCount > numericCount) return 'category';
  if (stringCount > numericCount * 2) return 'category';
  
  if (numericCount > sampleValues.length * 0.5) {
    const numValues = sampleValues
      .map(v => parseFloat(String(v).replace(/[,%$]/g, '')))
      .filter(n => !isNaN(n));
    
    const hasPercentSign = sampleValues.some(v => String(v).includes('%'));
    if (hasPercentSign) return 'percent';
    
    const allInRange = numValues.every(n => n >= 0 && n <= 100);
    const avgValue = numValues.reduce((a, b) => a + b, 0) / numValues.length;
    
    if (allInRange && avgValue < 100 && avgValue > 0) return 'percent';
    if (avgValue > 50 && avgValue < 300 && numValues.every(n => n > 30 && n < 500)) return 'index';
    
    return 'count';
  }
  
  return 'ignore';
}
